import { useCallback, useEffect, useState } from "react";
import { fetchBillingSummary } from "Services/CommonServices";
import { BillingSummary } from "types/perSeat";

export interface BillingSummaryState {
  summary: BillingSummary | null;
  isLoading: boolean;
  /** No active subscription, or the call failed — show nothing either way. */
  hasFailed: boolean;
  refresh: () => Promise<void>;
}

/**
 * Billing overview while `enabled` is true: what the subscription costs per
 * cycle, the credit sitting on the account, and the net due next renewal.
 *
 * The endpoint refuses accounts without an active subscription (trial,
 * cancelled), so a failure is expected rather than exceptional — it simply
 * means there is no credit UI to show.
 */
export const useBillingSummary = (enabled: boolean): BillingSummaryState => {
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [hasFailed, setFailed] = useState<boolean>(false);

  const load = useCallback(async () => {
    if (!enabled) {
      setSummary(null);
      setFailed(false);
      return;
    }
    try {
      setLoading(true);
      setFailed(false);
      const response = await fetchBillingSummary();
      setSummary(response ?? null);
    } catch {
      setSummary(null);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { summary, isLoading, hasFailed, refresh: load };
};

export default useBillingSummary;
